'use client';

import React from 'react';
import { styled } from '@linaria/react';

import { PROMO_ENDS, Promo } from '@/constants';
import useInterval from '@/hooks/use-interval';

import TextLink from '@/components/Link/TextLink';
import Superheader, {
  Props as SuperheaderProps,
} from './Superheader';

interface Props extends Omit<SuperheaderProps, 'superheaderId'> {
  promo: Promo;
}

function BiAnnualSaleSuperheader(props: Props) {
  const [timeRemaining, setTimeRemaining] = React.useState(() => {
    if (PROMO_ENDS === null) {
      return 0;
    }

    const now = new Date();
    return PROMO_ENDS.getTime() - now.getTime();
  });

  useInterval(
    () => {
      if (PROMO_ENDS === null) {
        return;
      }
      const now = new Date();
      setTimeRemaining(PROMO_ENDS.getTime() - now.getTime());
    },
    timeRemaining > 0 ? 1000 : null
  );

  const isBiAnnualSale =
    props.promo === 'black-friday' || props.promo === 'spring';

  if (!isBiAnnualSale || timeRemaining < 0) {
    return null;
  }

  const saleName =
    props.promo === 'black-friday'
      ? 'Black Friday sale'
      : 'Spring Sale';
  const emoji = props.promo === 'black-friday' ? '🎈' : '🌸';

  return (
    <Superheader {...props} superheaderId="bi-annual-sale">
      <Desktop>
        <TextLink
          href="https://www.joyforjs.com/"
          target="_blank"
          rel="noopener"
        >
          {emoji} I’m running my annual {saleName}!
        </TextLink>{' '}
        <ExtraText suppressHydrationWarning>
          Ends {getRelativeTimeRemaining(timeRemaining)}
        </ExtraText>
      </Desktop>
      <Mobile>
        <TextLink
          href="https://www.joyforjs.com/"
          target="_blank"
          rel="noopener"
        >
          I’m running my annual {saleName}!
        </TextLink>
      </Mobile>
    </Superheader>
  );
}

function getRelativeTimeRemaining(timeRemaining: number) {
  const distanceInSeconds = Math.floor(timeRemaining / 1000);
  const distanceInMinutes = Math.floor(distanceInSeconds / 60);
  const distanceInHours = Math.floor(distanceInMinutes / 60);
  const distanceInDays = Math.floor(distanceInHours / 24);

  let message;

  if (distanceInHours > 24) {
    message = `in ${distanceInDays} day${
      distanceInDays === 1 ? '' : 's'
    }.`;
  } else if (distanceInHours >= 1) {
    message = `in ${distanceInHours} hour${
      distanceInHours === 1 ? '' : 's'
    }.`;
  } else if (distanceInMinutes >= 1) {
    message = `in ${distanceInMinutes} minute${
      distanceInMinutes === 1 ? '' : 's'
    }!`;
  } else {
    message = `in ${distanceInSeconds} second${
      distanceInSeconds === 1 ? '' : 's'
    }!`;
  }

  return message;
}

const Desktop = styled.span`
  display: block;
  @media (max-width: 34rem) {
    display: none;
  }
`;

const Mobile = styled.span`
  display: none;
  @media (max-width: 34rem) {
    display: block;
  }
`;

const ExtraText = styled.span`
  @media (max-width: 48rem) {
    display: none;
  }
`;

export default BiAnnualSaleSuperheader;
